import axios from "axios";
import debounce from "lodash.debounce";
import React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { connect } from "react-redux";
import styled from "styled-components";
import Autocomplete from "src/components/shared/Autocomplete";
import { ApplicationStore } from "src/store";
import {
    getTradePendingDetailsRequest,
    toggleTradeInVehicleModeDefault,
    toggleTradeInVehicleModeFallback,
} from "src/store/actions/tradeIn";
import { VehicleInputMode } from "src/store/reducers/tradeIn";
import { getCountryFromAccount } from "src/utils/account";

let manualModeTimeout: number | undefined;

function clearManualModeTimeout() {
    if (manualModeTimeout) {
        clearTimeout(manualModeTimeout);
        manualModeTimeout = undefined;
    }
}

const Root = styled("div")`
    width: auto;
`;

type VehicleItem = Vehicle & { label: string };

export interface AdditionalProps {
    onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

interface State {
    results: VehicleItem[];
    searching: boolean;
}

interface Vehicle {
    id?: string;
    year: string;
    make: string;
    model: string;
    trim?: string;
    ymmt: string;
}

const mapStateToProps = ({ account, deal, tradeIn }: ApplicationStore) => {
    return {
        country: getCountryFromAccount(account),
        uuid: deal.uuid as string,
        partner_id: account.integration_settings?.TP_TradeEstimate?.partner_id as string,
        vehicleMode: tradeIn.vehicleInputMode,
    };
};

const mapDispatchToProps = {
    getTradePendingDetails: getTradePendingDetailsRequest,
    useTradePendingFallbackMode: toggleTradeInVehicleModeFallback,
    useTradePendingDefaultMode: toggleTradeInVehicleModeDefault,
};

class VehicleSearch extends React.Component<
    ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps & AdditionalProps & WithTranslation,
    State
> {
    public state = {
        results: [],
        searching: false,
    };

    public handleOnInputDebounce = debounce(async (value: string) => {
        this.startManualModeTimeout();

        try {
            const { data } = await axios.get<Vehicle[]>(
                `https://snap-api.tradepending.com/api/v4/ymmtsearch?query=${encodeURI(value)}&partner_id=${
                    this.props.partner_id
                }&result_count=5&country=${this.props.country}`,
                {
                    headers: {
                        Authorization: "Basic " + btoa("turncar-two-snap-access:bximuxmt0fmectt6fucw89f7fhd3nshe"),
                        "Content-Type": "application/json",
                    },
                }
            );

            this.handleOnTradePendingSearchSuccess(
                data.map((result) => ({
                    ...result,
                    ymmt: `${result.year} ${result.make} ${result.model} ${result.trim}`,
                }))
            );
        } catch (error) {
            this.handleOnTradePendingSearchError();
        }
    }, 800);

    public handleOnInputChange = (value: string) => {
        if (!value) {
            this.setState({ results: [], searching: false });

            return;
        }

        this.setState({ searching: true });

        this.handleOnInputDebounce(value);
    };

    public startManualModeTimeout = () => {
        clearManualModeTimeout();

        manualModeTimeout = setTimeout(() => {
            this.props.useTradePendingFallbackMode();
        }, 10000);
    };

    public handleOnTradePendingSearchSuccess = (results: Vehicle[]) => {
        clearManualModeTimeout();

        this.setState({ results: results.map((m) => ({ ...m, label: m.ymmt })), searching: false });

        if (results.length === 0 && this.props.vehicleMode !== VehicleInputMode.Fallback) {
            this.props.useTradePendingFallbackMode();
            return;
        }

        if (this.props.vehicleMode !== VehicleInputMode.Default) {
            this.props.useTradePendingDefaultMode();
        }
    };

    public handleOnTradePendingSearchError = () => {
        this.setState({ results: [], searching: false });

        this.props.useTradePendingFallbackMode();
    };

    public handleOnVehicleSelected = (vehicle: VehicleItem) => {
        clearManualModeTimeout();
        this.props.getTradePendingDetails(
            {
                partner_id: this.props.partner_id,
                make: vehicle.make,
                model: vehicle.model,
                trim: vehicle.trim,
                year: vehicle.year,
                ymmt: `${vehicle.year} ${vehicle.make} ${vehicle.model} ${vehicle.trim}`,
                answers: {},
            },
            this.props.uuid,
            true
        );
    };

    public handleOnInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        this.props.onFocus && this.props.onFocus(e);
    };

    public render() {
        const { t } = this.props;
        return (
            <Root>
                <Autocomplete
                    count={5}
                    items={this.state.results}
                    onChange={this.handleOnInputChange}
                    onFocus={this.handleOnInputFocus}
                    onSelection={this.handleOnVehicleSelected}
                    searching={this.state.searching}
                    placeholder={t("TradePending.VehicleSearch.placeholder")}
                />
            </Root>
        );
    }
}

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(VehicleSearch));
